import { BUSINESS_PCT, clientTotal } from "../Data/offers";

// The boutique's cart (docs/business-plan.md): the same bag every customer
// has (localStorage "sel"), read as an order book. Each piece belongs to the
// client whose measurements it carries; a piece with none waits for them.
// Pieces the boutique keeps for another day sit in "sel-hold" and are not
// paid for tonight.

export const HOLD_KEY = "sel-hold";
export const CART_EVENT = "mv-cart-changed";

const safe = (n) => (Number.isFinite(Number(n)) ? Number(n) : 0);

const isPreset = (it) => Object.prototype.hasOwnProperty.call(it || {}, "item_type");

// Uniform lines, accessories and fabric samples need no body measurements.
export const needsNoMeasuring = (it) => isPreset(it) && (
    it.item_type === 0
    || it.productKind === "uniform"
    || it.productKind === "accessory"
    || it.measurementProfile === "none"
);

export const isMeasured = (it) => needsNoMeasuring(it) || [0, 1, 3].includes(it?.measurements?.type);

// Who the piece is for: the measurement profile's name, as Get Measure
// saves it on the piece.
export const clientOf = (it) => {
    const m = it?.measurements || {};
    const name = String(m.profileName || m.name || m.profile?.name || "").trim();
    if (name) return name;
    if (needsNoMeasuring(it)) return "For the studio";
    return "";
};

// Our price for one piece after its own discount (as checkout adds it up).
export const ourPrice = (it) => {
    if (!it) return 0;
    if (isPreset(it) && it.item_type === 0) return (Array.isArray(it.items) ? it.items.length : 0) > 1 ? 250 : 100;
    const price = safe(it.price);
    return isPreset(it) ? price - (price * safe(it.discount)) / 100 : price - safe(it.discount);
};
export const costOf = (it) => Math.round((ourPrice(it) * (100 - BUSINESS_PCT)) / 100);

// The day's pieces grouped by client, in the order they were added; pieces
// still to be measured come together last.
export const groupByClient = (items = []) => {
    const groups = [];
    const byKey = new Map();
    (Array.isArray(items) ? items : []).forEach((it) => {
        const measured = isMeasured(it);
        const name = measured ? clientOf(it) || "Client not named" : "";
        const key = measured ? `c:${name.toLowerCase()}` : "pending";
        if (!byKey.has(key)) {
            const g = { key, name: measured ? name : "To be measured", measured, items: [] };
            byKey.set(key, g);
            groups.push(g);
        }
        byKey.get(key).items.push(it);
    });
    return groups.sort((a, b) => (a.measured === b.measured ? 0 : a.measured ? -1 : 1));
};

// Tonight's measured pieces: our price, the boutique's cost, what its clients
// pay (our price plus the service charge) and what the boutique keeps.
export const dayTotals = (items = []) => {
    const ready = (Array.isArray(items) ? items : []).filter(isMeasured);
    const ours = ready.reduce((s, it) => s + ourPrice(it), 0);
    const cost = ready.reduce((s, it) => s + costOf(it), 0);
    const client = ready.reduce((s, it) => s + clientTotal(ourPrice(it)), 0);
    return { readyCount: ready.length, waitingCount: items.length - ready.length, ours, cost, client, earn: client - cost };
};

export const initials = (name) => {
    const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return "?";
    return (parts.length > 1 ? parts[0][0] + parts[1][0] : parts[0].slice(0, 2)).toUpperCase();
};

const readList = (key) => {
    try { const v = JSON.parse(localStorage.getItem(key) || "[]"); return Array.isArray(v) ? v : []; } catch (e) { return []; }
};
const writeList = (key, list) => {
    try { localStorage.setItem(key, JSON.stringify(list)); } catch (e) { /* full or private */ }
    window.dispatchEvent(new Event(CART_EVENT));
};
export const readCart = () => readList("sel");
export const readHold = () => readList(HOLD_KEY);

// Move a piece out of tonight's cart, or bring it back.
export const holdPiece = (id) => {
    const cart = readCart();
    const piece = cart.find((it) => String(it?.id) === String(id));
    if (!piece) return cart;
    const next = cart.filter((it) => it !== piece);
    writeList(HOLD_KEY, [...readHold(), { ...piece, heldAt: Date.now() }]);
    writeList("sel", next);
    return next;
};
export const unholdPiece = (id) => {
    const hold = readHold();
    const piece = hold.find((it) => String(it?.id) === String(id));
    if (!piece) return readCart();
    const { heldAt, ...back } = piece;
    const next = [...readCart(), back];
    writeList(HOLD_KEY, hold.filter((it) => it !== piece));
    writeList("sel", next);
    return next;
};

// Time left to pay tonight (to 11:59 pm), as "7 h 12 min".
export const timeToMidnight = (now = new Date()) => {
    const end = new Date(now); end.setHours(23, 59, 59, 999);
    const mins = Math.max(0, Math.floor((end - now) / 60000));
    const h = Math.floor(mins / 60);
    return h ? `${h} h ${mins % 60} min` : `${mins} min`;
};
